<template>
  <Container
    id="heading"
  >
    <HeroCard
      :heading="t('jc.view.audioDesigner.hero.title')"
      :paragraph="t('jc.view.audioDesigner.hero.text')"
      :image="{
        src: banner,
        alt: t('jc.view.audioDesigner.hero.banner.alt'),
      }"
      :float="{
        src: float,
        alt: t('jc.view.audioDesigner.hero.float.alt'),
      }"
      :label="t('jc.view.audioDesigner.hero.button.label')"
      :icon="{ prefix: 'fab', iconName: 'discord' }"
      :to="config.public.discordParticipateUrl"
    />
  </Container>

  <Container
    id="our-offer"
  >
    <ContentCard
      :heading="t('jc.view.audioDesigner.ourOffer.title')"
      :paragraph="t('jc.view.audioDesigner.ourOffer.text')"
    >
      <IconWidget
        :items="ourOfferHighlightItems"
        class="my-12"
      />
      <IconList
        :items="ourOfferItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="our-conditions"
  >
    <ContentCard
      :heading="t('jc.view.audioDesigner.ourConditions.title')"
      :paragraph="t('jc.view.audioDesigner.ourConditions.text')"
    >
      <IconList
        :items="ourConditionsItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="your-tasks"
  >
    <ContentCard
      :heading="t('jc.view.audioDesigner.yourTasks.title')"
      :paragraph="t('jc.view.audioDesigner.yourTasks.text')"
    >
      <IconList
        :items="yourTasksItems"
      />
    </ContentCard>
  </Container>

  <Container
    id="impressions"
  >
    <ContentCard
      :heading="t('jc.view.audioDesigner.impressions.title')"
      :paragraph="t('jc.view.audioDesigner.impressions.text')"
    >
      <div
        class="grid grid-cols-1 laptop:grid-cols-2 gap-x-4 gap-y-8"
      >
        <template
          v-for="({ identifier, ...resource }, index) in impressionsResources"
          :key="index"
        >
          <DynamicResource
            :resource="resource"
            :caption="t(`jc.view.audioDesigner.impressions.${identifier}.text`)"
            aspect-video
            rounded
          />
        </template>
      </div>
    </ContentCard>
  </Container>

  <Container
    id="how-to-apply"
  >
    <ContentCard
      :heading="t('jc.view.audioDesigner.howToApply.title')"
      :paragraph="t('jc.view.audioDesigner.howToApply.text')"
    >
      <IconSection
        :sections="howToApplySections"
      />
    </ContentCard>
  </Container>

  <Container
    id="more-information"
  >
    <div
      class="flex flex-col gap-8"
    >
      <ContentCard
        :heading="t('jc.view.audioDesigner.moreInformation.title')"
        :paragraph="t('jc.view.audioDesigner.moreInformation.text')"
      />
      <div
        class="grid grid-cols-1 mobile:gird-cols-2 laptop:grid-cols-3 desktop:grid-cols-4 gap-x-4 gap-y-16 justify-center"
      >
        <template
          v-for="(card, index) in moreInformationCards"
          :key="index"
        >
          <ShowcaseCard
            v-bind="card"
          />
        </template>
      </div>
    </div>
  </Container>

  <Container
    id="callToAction"
    :background="'white'"
  >
    <ParticipateCallCard />
  </Container>
</template>

<script setup lang="ts">
const banner = 'images/component/participateCallCard/background.png'
const float = 'images/skin_renders/roles/audio_designer.png'

definePageMeta({
  nameKey: 'participate-audioDesigner',
})

const localePath = useLocalePath()

const { t } = useI18n()
const config = useRuntimeConfig()

const ourOfferHighlightItems = [
  {
    icon: 'users',
    text: t('jc.view.audioDesigner.ourOffer.iconHighlight.atmosphere.text'),
  },
  {
    icon: 'dragon',
    text: t('jc.view.audioDesigner.ourOffer.iconHighlight.creativeDevelopment.text'),
  },
  {
    icon: 'flask-round-potion',
    text: t('jc.view.audioDesigner.ourOffer.iconHighlight.innovativeConcepts.text'),
  },
  {
    icon: 'brain-circuit',
    text: t('jc.view.audioDesigner.ourOffer.iconHighlight.newTechniques.text'),
  },
  {
    icon: 'cubes',
    text: t('jc.view.audioDesigner.ourOffer.iconHighlight.resourcepack.text'),
  },
  {
    icon: 'campground',
    text: t('jc.view.audioDesigner.ourOffer.iconHighlight.teamEvents.text'),
  },
]

const ourOfferItems = [
  {
    icon: 'shield-check',
    text: t('jc.view.audioDesigner.ourOffer.icon.securities.text'),
  },
  {
    icon: 'brain-arrow-curved-right',
    text: t('jc.view.audioDesigner.ourOffer.icon.coDecide.text'),
  },
  {
    icon: 'sitemap',
    text: t('jc.view.audioDesigner.ourOffer.icon.hierarchy.text'),
  },
  {
    icon: 'clock',
    text: t('jc.view.audioDesigner.ourOffer.icon.timeManagement.text'),
  },
  {
    icon: 'toolbox',
    text: t('jc.view.audioDesigner.ourOffer.icon.teamTools.text'),
  },
  {
    icon: 'image-portrait',
    text: t('jc.view.audioDesigner.ourOffer.icon.portfolio.text'),
  },
  {
    icon: 'file-certificate',
    text: t('jc.view.audioDesigner.ourOffer.icon.licences.text'),
  },
  {
    icon: 'paintbrush-pencil',
    text: t('jc.view.audioDesigner.ourOffer.icon.passionatePeople.text'),
  },
  {
    icon: 'gears',
    text: t('jc.view.audioDesigner.ourOffer.icon.supportedWorkflows.text'),
  },
  {
    icon: 'handshake',
    text: t('jc.view.audioDesigner.ourOffer.icon.closeCooperation.text'),
  },
]

const ourConditionsItems = [
  {
    icon: 'couch',
    text: t('jc.view.audioDesigner.ourConditions.icon.teamSpirit.text'),
  },
  {
    icon: 'alicorn',
    text: t('jc.view.audioDesigner.ourConditions.icon.fantasy.text'),
  },
  {
    icon: 'book-open-reader',
    text: t('jc.view.audioDesigner.ourConditions.icon.learn.text'),
  },
  {
    icon: 'gauge-max',
    text: t('jc.view.audioDesigner.ourConditions.icon.motivation.text'),
  },
  {
    icon: 'comment-smile',
    text: t('jc.view.audioDesigner.ourConditions.icon.communication.text'),
  },
  {
    icon: 'battery-full',
    text: t('jc.view.audioDesigner.ourConditions.icon.endurance.text'),
  },
]

const yourTasksItems = [
  {
    icon: 'music',
    text: t('jc.view.audioDesigner.yourTasks.icon.compose.text'),
  },
  {
    icon: 'guitar',
    text: t('jc.view.audioDesigner.yourTasks.icon.design.text'),
  },
  {
    icon: 'cassette-tape',
    text: t('jc.view.audioDesigner.yourTasks.icon.mix.text'),
  },
  {
    icon: 'microphone-stand',
    text: t('jc.view.audioDesigner.yourTasks.icon.use.text'),
  },
  {
    icon: 'sparkles',
    text: t('jc.view.audioDesigner.yourTasks.icon.create.text'),
  },
  {
    icon: 'stamp',
    text: t('jc.view.audioDesigner.yourTasks.icon.shape.text'),
  },
]

const impressionsResources = [
  {
    identifier: 'hub',
    mediaType: 'image/*',
    src: 'images/impressions/general/hub.png',
  },
  {
    identifier: 'clashlandsHub',
    mediaType: 'image/*',
    src: 'images/impressions/general/pvp_hub.png',
  },
  {
    identifier: 'dungeonRoom',
    mediaType: 'image/*',
    src: 'images/impressions/general/dungeon_room.png',
  },
  {
    identifier: 'conquestMap',
    mediaType: 'image/*',
    src: 'images/impressions/general/conquest_map.png',
  },
  {
    identifier: 'cosmeticMenu',
    mediaType: 'image/*',
    src: 'images/impressions/general/cosmetic_menu.png',
  },
  {
    identifier: 'clashlandsCannon',
    mediaType: 'image/*',
    src: 'images/impressions/general/cannon.png',
  },
  {
    identifier: 'contentplate',
    mediaType: 'image/*',
    src: 'images/impressions/audio_designer/contentplate.png',
  },
  {
    identifier: 'timelapse',
    mediaType: 'video/*',
    src: '/images/impressions/audio_designer/timelapse.mp4',
    options: {
      controls: true,
      muted: true,
      autoplay: true,
    },
  },
]

const howToApplySections = [
  {
    icon: 'hand-wave',
    title: t('jc.view.audioDesigner.howToApply.icon.firstContact.title'),
    text: t('jc.view.audioDesigner.howToApply.icon.firstContact.text'),
  },
  {
    icon: 'comments',
    title: t('jc.view.audioDesigner.howToApply.icon.conversation.title'),
    text: t('jc.view.audioDesigner.howToApply.icon.conversation.text'),
  },
  {
    icon: 'envelope',
    title: t('jc.view.audioDesigner.howToApply.icon.wait.title'),
    text: t('jc.view.audioDesigner.howToApply.icon.wait.text'),
  },
  {
    icon: 'user-graduate',
    title: t('jc.view.audioDesigner.howToApply.icon.trainee.title'),
    text: t('jc.view.audioDesigner.howToApply.icon.trainee.text'),
  },
  {
    icon: 'comment-smile',
    title: t('jc.view.audioDesigner.howToApply.icon.feedbackTalk.title'),
    text: t('jc.view.audioDesigner.howToApply.icon.feedbackTalk.text'),
  },
]

const moreInformationCards = [
  {
    icon: 'users',
    text: t('jc.view.audioDesigner.moreInformation.card.team.text'),
    label: t('jc.view.audioDesigner.moreInformation.card.team.button.label'),
    to: localePath({ name: 'team' }),
  },
  {
    icon: 'newspaper',
    text: t('jc.view.audioDesigner.moreInformation.card.weekly_review.text'),
    label: t('jc.view.audioDesigner.moreInformation.card.weekly_review.button.label'),
    to: localePath({ name: 'news' }),
  },
  {
    icon: 'signs-post',
    text: t('jc.view.audioDesigner.moreInformation.card.plan.text'),
    label: t('jc.view.audioDesigner.moreInformation.card.plan.button.label'),
    to: localePath({ name: 'game' }),
  },
  {
    icon: 'microchip',
    text: t('jc.view.audioDesigner.moreInformation.card.stackShare.text'),
    label: t('jc.view.audioDesigner.moreInformation.card.stackShare.button.label'),
    to: config.public.stackShareUrl,
  },
  {
    icon: 'cube',
    text: t('jc.view.audioDesigner.moreInformation.card.planetMinecraft.text'),
    label: t('jc.view.audioDesigner.moreInformation.card.planetMinecraft.button.label'),
    to: config.public.planetMinecraftUrl,
  },
  {
    icon: 'file-lines',
    text: t('jc.view.audioDesigner.moreInformation.card.blog.text'),
    label: t('jc.view.audioDesigner.moreInformation.card.blog.button.label'),
    to: config.public.scrayosUrl,
  },
]
</script>
